import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, CheckCircle, Loader2, ShieldCheck } from 'lucide-react';
import { setAssessorPin, getAssessorPinStatus } from 'zite-endpoints-sdk';
import { toast } from 'sonner';

export default function AssessorPinSetup() {
  const [hasPin, setHasPin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getAssessorPinStatus({}).then(r => {
      setHasPin(r.hasPin);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) { toast.error('PIN must be exactly 4 digits'); return; }
    if (pin !== confirmPin) { toast.error('PINs do not match'); return; }
    setSaving(true);
    try {
      await setAssessorPin({ pin, currentPin: hasPin ? currentPin : undefined });
      toast.success('Assessor PIN set successfully');
      setHasPin(true);
      setEditing(false);
      setPin('');
      setConfirmPin('');
      setCurrentPin('');
    } catch (e: any) {
      toast.error(e.message || 'Failed to set PIN');
    }
    setSaving(false);
  };

  if (loading) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-primary" /> Assessor PIN
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!editing ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {hasPin ? (
                <><CheckCircle className="w-4 h-4 text-primary" /><span className="text-sm text-foreground">PIN is set</span></>
              ) : (
                <><Lock className="w-4 h-4 text-muted-foreground" /><span className="text-sm text-muted-foreground">No PIN set — required for assessments</span></>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              {hasPin ? 'Change' : 'Set PIN'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {hasPin && (
              <div>
                <Label className="text-xs">Current PIN</Label>
                <Input type="password" inputMode="numeric" maxLength={4} value={currentPin}
                  onChange={e => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="••••" />
              </div>
            )}
            <div>
              <Label className="text-xs">New PIN (4 digits)</Label>
              <Input type="password" inputMode="numeric" maxLength={4} value={pin}
                onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="••••" />
            </div>
            <div>
              <Label className="text-xs">Confirm PIN</Label>
              <Input type="password" inputMode="numeric" maxLength={4} value={confirmPin}
                onChange={e => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="••••" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={saving || pin.length !== 4 || confirmPin.length !== 4}>
                {saving ? <><Loader2 className="w-3 h-3 animate-spin mr-1" />Saving…</> : 'Save PIN'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setPin(''); setConfirmPin(''); setCurrentPin(''); }}>Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
